/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import type { ItemData } from './Types';

import Localizer from '../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { buildTitle } from '../../../helpers/ui/metadata/Format';
import TextScroller from '../../textScroller/TextScroller';
import './ItemIndex.css';
import Tooltip from '../../tooltip/Tooltip';

type ItemIndexPropType = {
  +currentIndex: number,
  +index: number,
  +isVideo: boolean,
  +itemData: ItemData | null,
  +onClick: (index: number) => void,
};

const PADDING_LENGTH = 2;

const ItemIndex = ({ currentIndex, index, isVideo, itemData, onClick }: ItemIndexPropType): React.Node => {
  const isDebugModeEnabled = useSelector<boolean>((state) => state.appConfiguration.isDebugModeEnabled);

  const [progress, setProgress] = useState<number>(0);

  const indexRef = React.useRef(index);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const resetProgress = useCallback(() => {
    setProgress(0);
  }, []);

  const updateProgress = useCallback((updateIndex: number, percent: number) => {
    if (indexRef.current === updateIndex) {
      setProgress(percent);
    }
  }, []);

  useEffect(() => {
    Messenger.on(MessengerEvents.VIDEO_CAROUSEL_UPDATE_PROGRESS, updateProgress);

    return () => {
      Messenger.off(MessengerEvents.VIDEO_CAROUSEL_UPDATE_PROGRESS, updateProgress);
    };
  }, [updateProgress]);

  useEffect(() => {
    resetProgress();
  }, [currentIndex, resetProgress]);

  const handleOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();

      onClick(index);
    },
    [index, onClick],
  );

  const title = useMemo(() => {
    if (!itemData) {
      return '';
    }

    const { programMetadata, seriesMetadata } = itemData;
    return buildTitle(Localizer.language, programMetadata ?? null, seriesMetadata ?? null, true);
  }, [itemData]);

  const renderTitleElement = useCallback((): React.Node => {
    if (!itemData) {
      return <div className='title'>&nbsp;</div>;
    }

    return (
      <div className='titleContainer'>
        <TextScroller scrollOnMount style={{ font: '14px var(--regular-font)', maxHeight: '16px' }}>
          <div className='title'>{title}</div>
        </TextScroller>
      </div>
    );
  }, [title]);

  const indexText = (index + 1).toString().padStart(PADDING_LENGTH, '0');
  const progressStyle = { width: `${progress}%` };

  const tooltipContent = useMemo(() => {
    if (!itemData || !isDebugModeEnabled) {
      return null;
    }

    return (
      <div className={clsx('tooltipContent', 'carouselIndex', itemData.tileType)}>
        <img alt='' className={clsx(itemData.tileType)} src={itemData.imageUrl ?? null} />
        {renderTitleElement()}
      </div>
    );
  }, [isDebugModeEnabled, itemData, renderTitleElement]);

  const indexElement = (
    <button
      aria-label={Localizer.localize(isVideo ? 'accessibility.navigation.carousel_watch_trailer_for' : 'accessibility.navigation.carousel_see_cover_for', { title })}
      className={clsx('itemIndex', index === currentIndex && 'focused')}
      onClick={handleOnClick}
      type='button'
    >
      <div aria-hidden className='videoProgressContainer'>
        <div className='videoProgress' style={progressStyle} />
      </div>
      <div className='index'>
        <p>{indexText}</p>
      </div>
    </button>
  );

  // In debug mode, render a tooltip containing image and title for current program
  if (tooltipContent) {
    return <Tooltip content={tooltipContent}>{indexElement}</Tooltip>;
  }

  return indexElement;
};

export default ItemIndex;
